import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react"
import { useBlocker } from "react-router-dom"
import { toast } from "sonner"
import { blockBenefitNavigation, setBenefitEditorOpen } from "@/lib/benefit-edit-guard"
import { useWorkspaceEditorActivity } from "@/features/benefit-workspace/benefitEditorContext"

const EditContext = createContext<{ register: (id: string, open: boolean) => boolean; hasOpenEditors: boolean } | null>(null)

/** Reuse an enclosing scope so wizard and application-shell controls observe the same editors. */
export function BenefitEditScope({ children, additionalNavigationBlocked }: { children: ReactNode; additionalNavigationBlocked?: () => boolean }) {
  const enclosing = useContext(EditContext)
  return enclosing ? <>{children}</> : <BenefitEditScopeOwner additionalNavigationBlocked={additionalNavigationBlocked}>{children}</BenefitEditScopeOwner>
}

/** Owns open editors and blocks links, browser Back and step navigation until saved/cancelled. */
function BenefitEditScopeOwner({ children, additionalNavigationBlocked }: { children: ReactNode; additionalNavigationBlocked?: () => boolean }) {
  const additional = useRef(additionalNavigationBlocked)
  additional.current = additionalNavigationBlocked
  const editors = useRef(new Set<string>())
  const [count, setCount] = useState(0)
  const register = useCallback((id: string, open: boolean) => {
    // One benefit response carries one optimistic token. Keeping two editors open would make
    // the second save predictably stale; reject it before a modal can capture an unusable token.
    if (open && editors.current.size > 0 && !editors.current.has(id)) return false
    if (setBenefitEditorOpen(editors.current, id, open)) setCount(editors.current.size)
    return true
  }, [])
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    blockBenefitNavigation(editors.current.size + (additional.current?.() ? 1 : 0), currentLocation, nextLocation))
  useEffect(() => {
    if (blocker.state === "blocked") {
      toast.error("Save or cancel the open editor before leaving this step.")
      blocker.reset()
    }
  }, [blocker])
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (editors.current.size > 0 || additional.current?.()) { event.preventDefault(); event.returnValue = "" }
    }
    window.addEventListener("beforeunload", beforeUnload)
    return () => window.removeEventListener("beforeunload", beforeUnload)
  }, [])
  const value = useMemo(() => ({ register, hasOpenEditors: count > 0 }), [register, count])
  return <EditContext.Provider value={value}>{children}</EditContext.Provider>
}

export function useBenefitEditRegistration() {
  const register = useContext(EditContext)?.register
  const id = useId()
  useEffect(() => () => { register?.(id, false) }, [register, id])
  return useCallback((open: boolean) => register?.(id, open) ?? true, [register, id])
}

export function useHasOpenBenefitEditors() {
  const workspace = useWorkspaceEditorActivity()
  return (useContext(EditContext)?.hasOpenEditors ?? false) || workspace
}
